import { Caption, Card, CardBody, Divider, Label, Row, Title } from './ui';
import Pill from '../../Lifecycle/shared/Pill';
import { formatDay } from '../../Lifecycle/shared/format';
import {
  channelLabel,
  consentSourceLabel,
  optOutSourceLabel,
} from '../../Lifecycle/shared/labels';

import Button from '@/components/Button';
import { WorkerPreferences } from '@/services/lifecycleAPI';
import styled from '@/styles';

const Grid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '70px 1fr',
  gap: '10px 12px',
  alignItems: 'center',
});

type Props = {
  preferences: WorkerPreferences;
  paused: boolean;
  doNotContact: boolean;
  onPause: () => void;
  onDoNotContact: () => void;
};

const ConsentCard = ({
  preferences,
  paused,
  doNotContact,
  onPause,
  onDoNotContact,
}: Props) => {
  // The send decision also blocks a number that sent STOP to Shift Blast or
  // before this engine, so that opt-out wins over the consent (review A-03).
  const phoneOptOut = preferences.sms_phone_opt_out;
  // An opt-out can come with no source; leave the brackets out rather than
  // print "(null)" or "()" (review C-04).
  const sourceNote = (source: string | null) =>
    source && `(${optOutSourceLabel(source)})`;
  const sms = preferences.sms_opted_out_at
    ? {
        tone: 'red' as const,
        text: [
          'Opted out',
          formatDay(preferences.sms_opted_out_at),
          sourceNote(preferences.sms_opt_out_source),
        ]
          .filter(Boolean)
          .join(' '),
      }
    : phoneOptOut
      ? {
          tone: 'red' as const,
          text: [
            'Opted out',
            phoneOptOut.opted_out_at && formatDay(phoneOptOut.opted_out_at),
            sourceNote(phoneOptOut.source),
          ]
            .filter(Boolean)
            .join(' '),
        }
      : preferences.sms_consent_at
        ? {
            tone: 'green' as const,
            text: `Consent ${formatDay(preferences.sms_consent_at)} (${consentSourceLabel(preferences.sms_consent_source || '')})`,
          }
        : { tone: 'grey' as const, text: 'No consent' };
  // Sends skip a blank or placeholder (@gravywork.temp) email, so "Subscribed"
  // would overstate reach (review N-04). Only an explicit false counts: older
  // payloads have no field. An unsubscribe keeps its own pill.
  const email = preferences.email_unsubscribed_at
    ? {
        tone: 'grey' as const,
        text: `Unsubscribed ${formatDay(preferences.email_unsubscribed_at)}`,
      }
    : preferences.email_deliverable === false
      ? { tone: 'grey' as const, text: 'No email address' }
      : { tone: 'green' as const, text: 'Subscribed' };

  return (
    <Card
      aria-labelledby="consentCardTitle"
      css={{ flex: '1 1 340px' }}
      data-testid="consentCard"
    >
      <CardBody>
        <Title id="consentCardTitle">Can we reach this worker?</Title>
        <Grid>
          <Label css={{ fontWeight: 600 }}>{channelLabel('sms')}</Label>
          <span>
            <Pill tone={sms.tone}>{sms.text}</Pill>
          </span>
          <Label css={{ fontWeight: 600 }}>{channelLabel('push')}</Label>
          <span>
            <Pill tone={preferences.push_reachable ? 'green' : 'grey'}>
              {preferences.push_reachable ? 'Reachable' : 'No device'}
            </Pill>
          </span>
          <Label css={{ fontWeight: 600 }}>{channelLabel('email')}</Label>
          <span>
            <Pill tone={email.tone}>{email.text}</Pill>
          </span>
        </Grid>
        {(!paused || !doNotContact) && (
          <Divider>
            {!paused && (
              <Row>
                <Button
                  a11yLabel="Pause journeys"
                  data-testid="pauseJourneysBtn"
                  disabled={doNotContact}
                  label="Pause journeys"
                  onClick={onPause}
                />
                <Caption>
                  Stops journey messages and calls. Shift and pay messages still
                  go.
                </Caption>
              </Row>
            )}
            {!doNotContact && (
              <Row>
                <Button
                  a11yLabel="Do not contact"
                  appearance="outline"
                  data-testid="doNotContactBtn"
                  label="Do not contact"
                  status="danger"
                  onClick={onDoNotContact}
                />
                <Caption>
                  Stops every lifecycle message, journeys and transactional. Use
                  when the worker asks.
                </Caption>
              </Row>
            )}
          </Divider>
        )}
      </CardBody>
    </Card>
  );
};

export default ConsentCard;
